import {
	BasicModal,
	ButtonSpeedDial,
	FloatingLoader,
	Typography,
} from "@/components";
import useContactTeams from "@/hooks/useContactTeams";
import useProfile from "@/hooks/useProfile";
import { CONTACT_DIRECTORY_SCOPE } from "@/libraries/constant";
import { TeamPageSearchSchema } from "@/schemas";
import Button from "@mui/material/Button";
import {
	Link,
	createFileRoute,
	retainSearchParams,
} from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import EmptyTeams from "../../-components/EmptyTeams";
import ListSkeleton from "../../-components/ListSkeleton";
import TeamCard from "../../-components/TeamCard";
import { getContactTeamsLoader } from "../../-functions/loaders/teams";

export const Route = createFileRoute("/dashboard/contacts/contact/teams/")({
	validateSearch: zodSearchValidator(TeamPageSearchSchema),
	search: {
		middlewares: [retainSearchParams(["scope"])],
	},
	loader: (ctx) =>
		getContactTeamsLoader(ctx.context, {
			directory: false,
			// @ts-expect-error
			scope: ctx.location.search.scope ?? CONTACT_DIRECTORY_SCOPE.ALL_TEAMS,
		}),
	pendingComponent: ListSkeleton,
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const { scope, search, notAuthorized } = Route.useSearch({
		select: (state: TeamPageSearchSchema) => ({
			scope: state.scope ?? CONTACT_DIRECTORY_SCOPE.ALL_TEAMS,
			search: state.q ?? "",
			notAuthorized: state.notAuthorized,
		}),
	});
	const { profile } = useProfile();
	const contactTeams = useContactTeams({
		isDirectory: false,
		scope,
		search,
		paddingStart: 16,
		paddingEnd: 229,
	});
	const onClickAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		return navigate({
			to: "/dashboard/contacts/no-access",
		});
	};
	return (
		<>
			<FloatingLoader open={contactTeams.isLoading} />
			<div
				ref={contactTeams.scrollRef}
				className="relative h-full py-3 overflow-y-auto"
			>
				<div
					className="flex flex-col gap-4"
					style={{
						height: contactTeams.virtualizer.getTotalSize(),
					}}
				>
					{contactTeams.virtualizer.items.map((teamVirtualizerItem) => {
						const teamGroup = contactTeams.teams[teamVirtualizerItem.index];
						const teamMembers =
							teamGroup.type === "team" ? teamGroup.users : teamGroup.members;
						const teamMembersIds = teamMembers.map((t) => t.id);
						const isNotAuthorized = !teamMembersIds.includes(
							profile?.id as number,
						);
						return (
							<Link
								key={teamVirtualizerItem.key}
								to={
									isNotAuthorized
										? Route.fullPath
										: teamGroup.type === "team"
											? "/dashboard/teams/$id"
											: "/dashboard/chat/group/$id/detail"
								}
								params={
									isNotAuthorized
										? undefined
										: teamGroup.type === "team"
											? { id: teamGroup.id }
											: {
													id: teamGroup.id,
													channelName: teamGroup.channel_name,
												}
								}
								search={
									isNotAuthorized
										? { notAuthorized: isNotAuthorized }
										: undefined
								}
								preload={isNotAuthorized ? false : "intent"}
								className="container absolute top-0 left-0"
								style={{
									height: teamVirtualizerItem.size,
									transform: `translateY(${teamVirtualizerItem.start}px)`,
								}}
							>
								<TeamCard data={teamGroup} profileId={profile?.id} />
							</Link>
						);
					})}
				</div>
				{contactTeams.isEmptySearch ? (
					<EmptyTeams title={`No results found for '${search}'`} />
				) : null}
				{contactTeams.isEmpty ? <EmptyTeams onClickAdd={onClickAdd} /> : null}
				<BasicModal isOpen={notAuthorized === true}>
					<div className="flex flex-col justify-center items-center gap-3">
						<Typography fontSize={16} fontWeight={600}>
							You're not part of this group
						</Typography>
						<Button
							fullWidth
							variant="contained"
							color="primary"
							size="large"
							className="!rounded-full"
							onClick={() =>
								navigate({
									// @ts-expect-error
									search: (oldSearch) => ({
										...oldSearch,
										notAuthorized: undefined,
									}),
									replace: true,
								})
							}
						>
							OK
						</Button>
					</div>
				</BasicModal>
			</div>
			<ButtonSpeedDial margin={0} bottom={0} onClickAdd={onClickAdd} />
		</>
	);
}
